import { CardTypeEnum } from '@varo/graphql-schema-types';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import useBelieveAccount from 'features/believe/application/hooks/useBelieveAccount';
import useFetchCardAndUserDetails from 'features/cardManagement/hooks/useFetchCardAndUserDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useFlowReturnRoute from 'shared/hooks/useFlowReturnRoute';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const AddWalletScreen = () => {
  const router = useRouter();
  const { top } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'actionPageString',
  });

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const { cardId, isBelieveDay0Flow } = useLocalSearchParams<{
    cardId: string;
    isBelieveDay0Flow?: string;
  }>();
  const isBelieveDay0 = isBelieveDay0Flow === 'true';
  const subcategory = isBelieveDay0
    ? 'believe_day_0_application'
    : 'believe_application';
  const walletSubcategory = isBelieveDay0
    ? 'wallet_provisioning_believe_day_0'
    : 'wallet_provisioning';

  useViewDidFocusScreenAnalyticsEvent({
    key: 'addWallet',
    customAttributes: { subcategory },
  });

  const { account: believeAccount } = useBelieveAccount();
  const { exitToReturnRoute } = useFlowReturnRoute('believeApplication');

  const { cardDetails, userDetails, error, fetching, refetchBaseQuery } =
    useFetchCardAndUserDetails({
      cardId,
    });

  const handleSkipNowAnalytics = usePressableAnalytics({
    analyticsId: 'skipNow',
    customAttributes: {
      card_type: CardTypeEnum.ChargeCard.toLowerCase(),
      source_name:
        cardDetails?.type !== CardTypeEnum.ChargeCard
          ? 'card_services'
          : 'believe_digital_card',
      subcategory: walletSubcategory,
    },
  });
  const handleSkipAndRedirect = () => {
    handleSkipNowAnalytics();
    if (isBelieveDay0 && exitToReturnRoute()) {
      return;
    }
    if (isBelieveDay0 && believeAccount?.id) {
      router.replace(`/accounts/${believeAccount.id}`);
    } else {
      router.replace('/believe/apply/moneyIn');
    }
  };

  const walletProps = {
    cardId: cardId,
    maskedCardNumber: cardDetails?.maskedCardNumber ?? '',
    cardholderName: userDetails?.customerInfo?.name?.full ?? '',
    isDebit: cardDetails?.type === CardTypeEnum.DebitCard,
    analyticsContextOverride: {
      category: 'card',
      subCategory: walletSubcategory,
      sourceName:
        cardDetails?.type === CardTypeEnum.ChargeCard
          ? 'believe_digital_card'
          : translatedText('screenName'),
    },
    sentryPageName: 'Add Wallet Screen',
    isBelieveDay0Flow,
    believeAccountId: believeAccount?.id,
  };

  return (
    <View style={[styles.container, { paddingTop: top }]}>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <View style={styles.skipForNowSection}>
        <View>
          <Button
            label={
              isBelieveDay0
                ? translatedText('skip')
                : translatedText('skipForNow')
            }
            type="TEXT"
            onPress={handleSkipAndRedirect}
          />
        </View>
      </View>
      <ActionScreen
        addWalletButton={walletProps}
        titleText={translatedText('title')}
        visual={
          isBelieveDay0
            ? 'BELIEVE_DARK_CARD_A_ANIMATED'
            : 'BELIEVE_CARD_A_ANIMATED'
        }
        bodyContent={
          <>
            <BodyBaseText>{translatedText('addCard')}</BodyBaseText>
            {isBelieveDay0 ? (
              <BodyXSText style={styles.disclaimerText} numberOfLines={0}>
                {translatedText('visaDisclaimer')}
              </BodyXSText>
            ) : null}
          </>
        }
        titleTextStyle="extra-large"
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={refetchBaseQuery}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  disclaimerText: {
    marginTop: theme.spacing.m,
    color: theme.color.text.secondary,
  },
  skipForNowSection: {
    alignItems: 'flex-end',
    marginHorizontal: theme.spacing.l,
    marginTop: theme.spacing.s,
  },
});

export default AddWalletScreen;
